import { zodResolver } from '@hookform/resolvers/zod';
import {
  Autocomplete,
  CopyableText,
  Dialog,
  Form,
  FormButton,
  Input,
  string,
  useNotifications
} from '@ijoin/components';
import {
  ApiKeysEntityType,
  FidelityCarrierContractsCarrier,
  getGetAdminApiKeysQueryKey,
  useGetAdminApiKeyEntityTypes,
  useGetAdminApiKeyScopes,
  useGetAdminRecordKeepingOrganizations,
  useGetAdminRolloverProviders,
  useGetAnnuityCarriers,
  usePostAdminApiKey
} from '@ijoin/hooks';
import {
  Alert,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';

import { sortBy, startCase } from 'lodash';
import { FC, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { AdvisoryOrganizationAutocomplete } from './AdvisoryOrganizationAutocomplete';

type SelectOption = {
  label: string;
  value: string;
};

type CreateFormValues = {
  advisoryOrganizationId: null | string;
  annuityIntegrationId: null | string;
  entityType: null | string;
  fidelityCarrier: null | string;
  name: string;
  recordKeepingOrganizationId: null | string;
  rolloverProviderId: null | string;
  scopes: string[];
};

const resolver = zodResolver(
  z
    .object({
      advisoryOrganizationId: z.string().nullable(),
      annuityIntegrationId: z.string().nullable(),
      entityType: z.string().nullable(),
      fidelityCarrier: z.string().nullable(),
      name: string({ required: true }),
      recordKeepingOrganizationId: z.string().nullable(),
      rolloverProviderId: z.string().nullable(),
      scopes: z.array(z.string())
    })
    .superRefine((values, ctx) => {
      if (!values.entityType) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Entity type is required',
          path: ['entityType']
        });
      }

      if (
        values.entityType === 'advisoryOrganization' &&
        !values.advisoryOrganizationId
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Advisory organization is required',
          path: ['advisoryOrganizationId']
        });
      }

      if (
        values.entityType === 'annuityCarrier' &&
        !values.annuityIntegrationId
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Annuity carrier is required',
          path: ['annuityIntegrationId']
        });
      }

      if (
        values.entityType === 'recordKeeper' &&
        !values.recordKeepingOrganizationId
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Record keeping organization is required',
          path: ['recordKeepingOrganizationId']
        });
      }

      if (
        values.entityType === 'rolloverProvider' &&
        !values.rolloverProviderId
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Rollover provider is required',
          path: ['rolloverProviderId']
        });
      }

      if (
        values.entityType === 'thirdParty' &&
        values.scopes.some(scope => scope.startsWith('fidelity:contracts:')) &&
        !values.fidelityCarrier
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Fidelity carrier is required',
          path: ['fidelityCarrier']
        });
      }
    })
);

export const CreateApiKeyDialog: FC<{ onClose: () => void }> = props => {
  const queryClient = useQueryClient();
  const notifications = useNotifications();

  const [createdKey, setCreatedKey] = useState<null | string>(null);

  const entityTypes = useGetAdminApiKeyEntityTypes();
  const scopes = useGetAdminApiKeyScopes();
  const carriers = useGetAnnuityCarriers();
  const recordKeepingOrganizations = useGetAdminRecordKeepingOrganizations({
    take: 1000
  });
  const rolloverProviders = useGetAdminRolloverProviders();

  const methods = useForm<CreateFormValues>({
    defaultValues: {
      advisoryOrganizationId: null,
      annuityIntegrationId: null,
      entityType: null,
      fidelityCarrier: null,
      name: '',
      recordKeepingOrganizationId: null,
      rolloverProviderId: null,
      scopes: []
    },
    mode: 'all',
    resolver
  });

  const createApiKey = usePostAdminApiKey({
    mutation: {
      onError: () => notifications.error('Failed to create API key'),
      onSuccess: data => {
        setCreatedKey(data.apiKey);
        queryClient.invalidateQueries({
          queryKey: getGetAdminApiKeysQueryKey()
        });
      }
    }
  });

  const onEntityTypeChange = () => {
    methods.setValue('advisoryOrganizationId', null);
    methods.setValue('annuityIntegrationId', null);
    methods.setValue('fidelityCarrier', null);
    methods.setValue('recordKeepingOrganizationId', null);
    methods.setValue('rolloverProviderId', null);
    methods.setValue('scopes', []);
  };

  const onScopesChange = (selected: SelectOption[]) => {
    if (
      !selected.some(option => option.value.startsWith('fidelity:contracts:'))
    ) {
      methods.setValue('fidelityCarrier', null);
    }
  };

  const onSubmit = (data: CreateFormValues) => {
    createApiKey.mutate({
      data: {
        advisoryOrganizationId: data.advisoryOrganizationId ?? undefined,
        annuityIntegrationId: data.annuityIntegrationId ?? undefined,
        entityType: data.entityType as ApiKeysEntityType,
        fidelityCarrier:
          data.entityType === 'thirdParty'
            ? ((data.fidelityCarrier as FidelityCarrierContractsCarrier) ??
              undefined)
            : undefined,
        name: data.name,
        recordKeepingOrganizationId:
          data.recordKeepingOrganizationId ?? undefined,
        rolloverProviderId: data.rolloverProviderId ?? undefined,
        scopes: data.entityType === 'thirdParty' ? data.scopes : undefined
      }
    });
  };

  const selectedEntityType = methods.watch('entityType');
  const selectedScopes = methods.watch('scopes');
  const hasFidelityContractsScope = selectedScopes.some(scope =>
    scope.startsWith('fidelity:contracts:')
  );

  const fidelityCarrierOptions: SelectOption[] = Object.values(
    FidelityCarrierContractsCarrier
  ).map(value => ({ label: value, value }));

  return (
    <Dialog
      data-testid='createAdminApiKeyDialog'
      fullWidth
      isOpen
      maxWidth='sm'
      onClose={props.onClose}>
      {createdKey ? (
        <>
          <DialogTitle variant='h3'>API Key Created</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Alert severity='warning'>
                Copy this key now. You will not be able to see it again.
              </Alert>
              <Stack alignItems='center' direction='row' spacing={1}>
                <Typography
                  data-testid='createdAdminApiKeyValue'
                  sx={{
                    fontFamily: 'monospace',
                    fontSize: '0.875rem',
                    overflowWrap: 'break-word',
                    wordBreak: 'break-all'
                  }}
                  variant='body2'>
                  {createdKey}
                </Typography>
                <CopyableText value={createdKey} />
              </Stack>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button
              data-testid='closeAdminApiKeyDialog'
              onClick={props.onClose}
              variant='contained'>
              Done
            </Button>
          </DialogActions>
        </>
      ) : (
        <Form
          methods={methods}
          noValidate
          onSubmit={methods.handleSubmit(onSubmit)}>
          <DialogTitle variant='h3'>Generate API Key</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Input
                data-testid='adminApiKeyName'
                format='string'
                fullWidth
                hideError
                label='Key Name'
                name='name'
                required
                size='small'
              />
              <Autocomplete<SelectOption>
                data-testid='adminApiKeyEntityType'
                fullWidth
                getOptionValue={option => option.value}
                label='Entity Type'
                loading={entityTypes.isFetching}
                name='entityType'
                onChange={onEntityTypeChange}
                options={(entityTypes.data?.entityTypes ?? []).map(value => ({
                  label: startCase(value),
                  value
                }))}
                placeholder='Select entity type'
                required
              />
              {selectedEntityType === 'advisoryOrganization' && (
                <AdvisoryOrganizationAutocomplete
                  data-testid='adminApiKeyAdvisoryOrganization'
                  getOptionValue={option => option.value}
                  hideError={false}
                  name='advisoryOrganizationId'
                  placeholder='Select advisory organization'
                  required
                />
              )}
              {selectedEntityType === 'annuityCarrier' && (
                <Autocomplete<SelectOption>
                  data-testid='adminApiKeyCarrier'
                  fullWidth
                  getOptionValue={option => option.value}
                  label='Annuity Carrier'
                  loading={carriers.isFetching}
                  name='annuityIntegrationId'
                  options={(carriers.data ?? []).map(carrier => ({
                    label: carrier.description,
                    value: carrier.annuityIntegrationId
                  }))}
                  placeholder='Select annuity carrier'
                  required
                />
              )}
              {selectedEntityType === 'recordKeeper' && (
                <Autocomplete<SelectOption>
                  data-testid='adminApiKeyRecordKeepingOrganization'
                  fullWidth
                  getOptionValue={option => option.value}
                  label='Record Keeping Organization'
                  loading={recordKeepingOrganizations.isFetching}
                  name='recordKeepingOrganizationId'
                  options={(recordKeepingOrganizations.data?.data ?? []).map(
                    organization => ({
                      label: organization.name,
                      value: organization.recordKeepingOrganizationId
                    })
                  )}
                  placeholder='Select record keeping organization'
                  required
                />
              )}
              {selectedEntityType === 'rolloverProvider' && (
                <Autocomplete<SelectOption>
                  data-testid='adminApiKeyRolloverProvider'
                  fullWidth
                  getOptionValue={option => option.value}
                  label='Rollover Provider'
                  loading={rolloverProviders.isFetching}
                  name='rolloverProviderId'
                  options={(rolloverProviders.data ?? []).map(provider => ({
                    label: provider.name ?? provider.rolloverProviderId,
                    value: provider.rolloverProviderId
                  }))}
                  placeholder='Select rollover provider'
                  required
                />
              )}
              {selectedEntityType === 'thirdParty' && (
                <Autocomplete<SelectOption>
                  data-testid='adminApiKeyScopes'
                  fullWidth
                  getOptionValue={option => option.value}
                  label='Scopes'
                  loading={scopes.isFetching}
                  multiple
                  name='scopes'
                  noOptionsText='No scopes available'
                  onChange={onScopesChange}
                  options={sortBy(
                    (scopes.data?.scopes ?? []).map(scope => ({
                      label: scope.label,
                      value: scope.value
                    })),
                    'label'
                  )}
                  placeholder='Select scopes'
                />
              )}
              {selectedEntityType === 'thirdParty' &&
                hasFidelityContractsScope && (
                  <Autocomplete<SelectOption>
                    data-testid='adminApiKeyFidelityCarrier'
                    fullWidth
                    getOptionValue={option => option.value}
                    label='Fidelity Carrier'
                    name='fidelityCarrier'
                    options={fidelityCarrierOptions}
                    placeholder='Select Fidelity carrier'
                    required
                  />
                )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button
              data-testid='cancelAdminApiKey'
              onClick={props.onClose}
              variant='outlined'>
              Cancel
            </Button>
            <FormButton
              data-testid='saveAdminApiKey'
              triggerValidation
              variant='contained'>
              Generate
            </FormButton>
          </DialogActions>
        </Form>
      )}
    </Dialog>
  );
};

CreateApiKeyDialog.displayName = 'CreateApiKeyDialog';
